import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/**
 * Data hooks for the Glossary feature — the cinch_ops metrics dictionary.
 *
 * The whole dictionary loads in one `GET /glossary` call: it's small, and
 * having every term on the page makes cross-link resolution trivial (a
 * `related` slug always points at a row that's already loaded). Search and
 * filtering happen client-side.
 */

export type TermKind = "concept" | "metric" | "calculation" | "query";

export interface TermSource {
  label: string;
  path?: string | null;
}

export interface GlossaryTerm {
  id: string;
  slug: string;
  term: string;
  aliases: string[] | null;
  category: string;
  tags: string[];
  kinds: TermKind[];
  definition: string;
  equation: string | null;
  query: string | null;
  sources: TermSource[] | null;
  related: string[] | null;
  sort_order: number;
  status: TermStatus;
  approved_by: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type TermStatus = "in_review" | "approved";

export interface TermListResponse {
  items: GlossaryTerm[];
  total: number;
  tags: string[];
}

export interface TermInput {
  slug: string;
  term: string;
  aliases: string[] | null;
  category: string;
  tags: string[];
  kinds: TermKind[];
  definition: string;
  equation: string | null;
  query: string | null;
  sources: TermSource[] | null;
  related: string[] | null;
  sort_order: number;
}

const KEY = ["glossary", "list"] as const;

export function useGlossary() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<TermListResponse>("/glossary/"),
    staleTime: 5 * 60_000,
  });
}

export function useCreateTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TermInput) =>
      apiFetch<GlossaryTerm>("/glossary/terms", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TermInput }) =>
      apiFetch<GlossaryTerm>(`/glossary/terms/${id}`, { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/glossary/terms/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useApproveTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<GlossaryTerm>(`/glossary/terms/${id}/approve`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
